import { Injectable } from '@angular/core';
import { UsersService } from '../api/services/users.service';
import { PbxService } from '../api/services/pbx.service';
import { HolidayGreetingsService } from '../api/services/holiday-greetings.service';
import { PbxAccountsService as PbxAccountsApiService } from '../api/services/pbx-accounts.service';
import { PbxGroupsService as PbxGroupsApiService } from '../api/services/pbx-groups.service';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../core/security/auth.service';
import { HolidayGreetingsModel } from '../api/models/holiday-greetings-model';
import { CloudFileModel } from '../api/models/cloud-file-model';
import { LookupModel } from '../api/models/lookup-model';
import { PbxAccountModel } from '../api/models/pbx-account-model';
import { LookupService } from '../api/services/lookup.service';
import { E911DetailsModel } from '../api/models/e-911-details-model';
import { E911DetailsRequest } from '../api/models/e-911-details-request';
import { TextToSpeechRequest } from '../api/models/text-to-speech-request';
import { TextToSpeechResponse } from '../api/models/text-to-speech-response';
import { PbxAccountButtonsService as PbxAccountButtonsApiService } from '../api/services/pbx-account-buttons.service';
import { PbxAccountButtonTemplatesService as PbxAccountButtonTemplatesApiService } from '../api/services/pbx-account-button-templates.service';
import { PbxAccountE911Service as PbxAccountE911ApiService } from '../api/services/pbx-account-e-911.service';
import { PbxAccountVoicemailGreetingsService as PbxAccountVoicemailGreetingsApiService } from '../api/services';
import { PbxAccountTextToSpeechService as PbxAccountTextToSpeechApiService } from '../api/services';

import { firstValueFrom, lastValueFrom, Observable } from 'rxjs';
import {
  BooleanResponse,
  ButtonModel,
  E911DocumentModel,
  LookupGridResponse,
  PbxAccountButtonTemplateModel,
  PbxAccountDeviceButtonsModel,
  PbxAccountDevicesListModel,
  PbxAccountVoicemailGreeting,
  PbxActiveCallModel,
  PbxGroupModel,
  PbxWallboardLiveModel,
  PbxWallboardModel,
  SavePbxAccountButtonsRequest,
} from '../api/models';
import { WallboardService as WallboardApiService } from '../api/services/wallboard.service';
import { PbxGroupCallModel } from '../api/models/pbx-group-call-model';
import { EventsService } from '../events/events.service';

@Injectable()
export class LocalPbxService {
  public accountsLookupList: LookupModel[] = [];
  public extensionsLookupList: LookupModel[] = [];

  public readonly e911DocumentEvents = this.eventsService.e911DocumentEventEmitter.asObservable();

  cellTimes: Array<LookupModel> = [
    { id: '', text: "Don't call cell phone" },
    { id: '0', text: 'Immediately' },
    { id: '1', text: 'After 1 Second' },
    { id: '2', text: 'After 2 Seconds' },
    { id: '5', text: 'After 5 Seconds' },
    { id: '10', text: 'After 10 Seconds' },
    { id: '15', text: 'After 15 Seconds' },
    // {id: '20', text: 'After 20 Seconds'},
    // {id: '25', text: 'After 25 Seconds'},
    // {id: '30', text: 'After 30 Seconds'},
  ];

  cellGroupOptions: Array<LookupModel> = [
    { id: 'false', text: "Don't call cell phone" },
    { id: 'true', text: 'Include cell phone in the group' },
    { id: 'unreg', text: 'Only if the extension is unregistered' },
  ];

  mailboxTypes: Array<LookupModel> = [
    { id: 'true', text: 'Enabled' },
    { id: 'false', text: 'Disabled' },
  ];

  mailboxTimeOptions: Array<LookupModel> = [
    { id: '', text: 'Default' },
    { id: '1', text: 'After 1 Second' },
    { id: '2', text: 'After 2 Seconds' },
    { id: '5', text: 'After 5 Seconds' },
    { id: '10', text: 'After 10 Seconds' },
    { id: '15', text: 'After 15 Seconds' },
    { id: '20', text: 'After 20 Seconds' },
    { id: '30', text: 'After 30 Seconds' },
    { id: '45', text: 'After 45 Seconds' },
    { id: '60', text: 'After 60 Seconds' },
  ];

  mailboxTimeOptionsFiltered: Array<LookupModel> = [{ id: '60', text: 'After 60 Seconds' }];

  mailTypes: Array<LookupModel> = [
    { id: 'none', text: 'Do not send an email' },
    { id: 'plain', text: 'Send email notification without voicemail attachment' },
    { id: 'attach', text: 'Send email notification with voicemail attachment' },
  ];

  mailOptions: Array<LookupModel> = [
    { id: 'keep', text: 'Keep message as new message' },
    { id: 'read', text: 'Mark message as read' },
    { id: 'delete', text: 'Delete the message' },
  ];

  callForwardTypes: Array<LookupModel> = [{ id: '', text: 'All Calls' }];

  callForwardTimes: Array<LookupModel> = [{ id: '', text: 'No specific time excluded' }];

  voicemailGreetings: Array<LookupModel> = [
    { id: 'name', text: 'Name', metadata: '' },
    { id: 'personal1', text: 'Default Greeting', metadata: '1' },
    { id: 'personal2', text: 'Vacation Greeting', metadata: '2' },
    { id: 'personal3', text: 'Away Greeting', metadata: '3' },
    { id: 'personal4', text: 'Busy Greeting', metadata: '4' },
    { id: 'personal5', text: 'Custom Greeting', metadata: '5' },
    { id: 'anonymous', text: 'Anonymous', metadata: '', isSystem: true },
  ];

  // Stores filterActive state for each groupId
  private queueAgentsFilterActive: { [groupId: string]: boolean } = {};

  constructor(
    private apiUserService: UsersService,
    private apiPbxService: PbxService,
    private apiHolidayGreetingsService: HolidayGreetingsService,
    private pbxAccountButtonsApiService: PbxAccountButtonsApiService,
    private pbxAccountButtonTemplatesApiService: PbxAccountButtonTemplatesApiService,
    private pbxAccountE911ApiService: PbxAccountE911ApiService,
    private pbxAccountVoicemailGreetingsApiService: PbxAccountVoicemailGreetingsApiService,
    private pbxAccountTextToSpeechApiService: PbxAccountTextToSpeechApiService,
    private pbxAccountsApiService: PbxAccountsApiService,
    private pbxGroupsApiService: PbxGroupsApiService,
    private pbxWallboardApiService: WallboardApiService,
    private httpClient: HttpClient,
    private lookupService: LookupService,
    private authService: AuthService,
    private eventsService: EventsService,
  ) {}

  public async getUserSession(): Promise<string> {
    const sessionId = await lastValueFrom(this.apiPbxService.pbxGetUserSession());

    if (sessionId) {
      const currentUser = this.authService.currentUser.getValue();
      await this.applySessionToken(currentUser.domain, sessionId);
    }

    return sessionId;
  }

  private async applySessionToken(domain: string, tempSessionId: string) {
    const options: any = {
      method: 'post',
      search: new URLSearchParams(),
      withCredentials: true,
    };

    const url = `https://${domain}/rest/system/session`;
    const body = JSON.stringify({
      name: 'session',
      value: tempSessionId,
    });

    return await lastValueFrom(this.httpClient.post<string>(url, body, options))
      .then(response => {
        return response;
      })
      .catch(e => {
        throw e;
      });
  }

  async getAvailableHolidayGreetings(): Promise<Array<CloudFileModel>> {
    return await lastValueFrom(this.apiHolidayGreetingsService.pbxHolidayGreetingsGetAvailableHolidayGreetings());
  }

  async getHolidayGreetingsDetails(account: string): Promise<HolidayGreetingsModel> {
    return await lastValueFrom(
      this.apiHolidayGreetingsService.pbxHolidayGreetingsGetHolidayGreetingsDetails({ account: account }),
    );
  }

  async saveHolidayGreetings(reqData: HolidayGreetingsModel): Promise<void> {
    await lastValueFrom(
      this.apiHolidayGreetingsService.pbxHolidayGreetingsSaveHolidayGreetingDetails({ body: reqData }),
    );
  }

  async getTextToSpeechVoices(): Promise<Array<LookupModel>> {
    const response = lastValueFrom(
      await this.pbxAccountTextToSpeechApiService.pbxAccountTextToSpeechGetTextToSpeechOptions(),
    );
    console.log(response);
    return response;
  }

  async getGeneratedHolidayGreeting(data: TextToSpeechRequest): Promise<TextToSpeechResponse> {
    return await lastValueFrom(
      this.pbxAccountTextToSpeechApiService.pbxAccountTextToSpeechGetTextToSpeechAudioStream({
        body: data,
      }),
    );
  }

  async savePbxAccount(reqData: PbxAccountModel): Promise<PbxAccountModel> {
    return await lastValueFrom(this.pbxAccountsApiService.pbxAccountsSaveAccountDetails({ body: reqData }));
  }

  getPbxAccountById(id: string): Observable<PbxAccountModel> {
    return this.pbxAccountsApiService.pbxAccountsGetAccountDetails({ id: id });
  }

  getPbxE911Addresses(): Observable<Array<E911DetailsModel>> {
    return this.pbxAccountE911ApiService.pbxAccountE911GetE911Addresses();
  }

  getE911Documents(params: { extension: string; eAni: string }): Observable<E911DocumentModel[]> {
    return this.pbxAccountE911ApiService.pbxAccountE911GetE911Documents(params);
  }

  getPbxAccountDevicesList(accountId: string): Observable<PbxAccountDevicesListModel[]> {
    return this.pbxAccountButtonsApiService.pbxAccountButtonsGetAccountDevicesList({ accountId });
  }

  getPbxAccountDeviceButtons(deviceId: string): Observable<PbxAccountDeviceButtonsModel> {
    return this.pbxAccountButtonsApiService.pbxAccountButtonsGetAccountDeviceButtons({
      id: deviceId,
    });
  }

  savePbxAccountButtonTemplate(template: PbxAccountButtonTemplateModel): Observable<void> {
    return this.pbxAccountButtonTemplatesApiService.pbxAccountButtonTemplatesSaveButtonTemplate({
      body: template,
    });
  }

  getPbxAccountButtonTemplateById(id: string): Observable<PbxAccountButtonTemplateModel> {
    return this.pbxAccountButtonTemplatesApiService.pbxAccountButtonTemplatesGetButtonTemplateById({ id });
  }

  deletePbxAccountButtonTemplateById(id: string): Observable<void> {
    return this.pbxAccountButtonTemplatesApiService.pbxAccountButtonTemplatesDeleteButtonTemplateById({ id });
  }

  savePbxAccountDeviceButtons(deviceId: string, data: SavePbxAccountButtonsRequest): Observable<void> {
    return this.pbxAccountButtonsApiService.pbxAccountButtonsSaveAccountDeviceButtons({ id: deviceId, body: data });
  }

  getPbxAccountVoicemailGreetings(extension: string): Observable<PbxAccountVoicemailGreeting[]> {
    return this.pbxAccountVoicemailGreetingsApiService.pbxAccountVoicemailGreetingsGetAccountVoicemailGreetings({
      extension,
    });
  }

  uploadPbxAccountVoicemailGreeting(extension: string, greeting: string, file: Blob): Observable<void> {
    return this.pbxAccountVoicemailGreetingsApiService.pbxAccountVoicemailGreetingsUploadAccountVoicemailGreeting({
      body: {
        extension,
        greeting,
        file,
      },
    });
  }

  requestE911Addresses(data: E911DetailsRequest): Observable<E911DocumentModel> {
    return this.pbxAccountE911ApiService.pbxAccountE911RequestE911Addresses({
      body: data,
    });
  }

  async getLookupLists() {
    const response = await this.lookupService.lookupGetCustomerExtensions({}).toPromise();
    const extensionsResponse = await lastValueFrom(
      this.lookupService.lookupGetCustomerExtensions({ types: ['extensions'] }),
    );

    this.accountsLookupList = response.data;
    this.extensionsLookupList = extensionsResponse.data;
  }

  getAccountsLookupForDevice(): Observable<LookupGridResponse> {
    return this.lookupService.lookupGetCustomerExtensions({});
  }

  async getWallboards(): Promise<PbxWallboardModel[]> {
    return await firstValueFrom(this.pbxWallboardApiService.pbxWallboardGetWallboards({}));
  }

  async getWallboardLiveData(id: string): Promise<PbxWallboardLiveModel> {
    return await firstValueFrom(this.pbxWallboardApiService.pbxWallboardGetWallboardInfo({ id: id }));
  }

  async getActiveGroupCalls(id: string): Promise<PbxGroupCallModel> {
    return await firstValueFrom(this.pbxWallboardApiService.pbxWallboardGetWallboardCalls({ id: id }));
  }

  async getActiveDomainCalls(): Promise<Array<PbxActiveCallModel>> {
    return await firstValueFrom(this.pbxWallboardApiService.pbxWallboardGetActiveDomainCalls());
  }

  async getPbxGroupById(id: string): Promise<PbxGroupModel> {
    return await firstValueFrom(this.pbxGroupsApiService.pbxGroupsGetAccountDetails({ id: id }));
  }

  async savePbxGroup(reqData: PbxGroupModel): Promise<PbxGroupModel> {
    return await firstValueFrom(this.pbxGroupsApiService.pbxGroupsSaveAccountDetails({ body: reqData }));
  }

  /**
   * Login/Logout Agent to Group.
   * @param groupId The group ID
   * @param extension The agent's extension
   * @param login true to login, false to logout
   */
  groupAgentLogin(groupId: string, extension: string, login: boolean): Promise<BooleanResponse> {
    return lastValueFrom(
      this.pbxWallboardApiService.pbxWallboardGroupAgentLogin({
        groupId: groupId,
        extension: extension,
        login: login ? 'true' : 'false',
      }),
    );
  }

  setQueueAgentsFilterActive(groupId: string, value: boolean) {
    if (groupId) {
      this.queueAgentsFilterActive[groupId] = value;
    }
  }

  getQueueAgentsFilterActive(groupId: string): boolean {
    return groupId ? !!this.queueAgentsFilterActive[groupId] : false;
  }
}
